"use client";

import { type ReactNode, createContext, useCallback, useContext, useRef } from "react";

interface ConversationalAIPageAgentContextType {
  registerStartCallAction: (action: () => void) => void;
  startCall: () => void;
}

const ConversationalAIPageAgentContext = createContext<ConversationalAIPageAgentContextType | null>(null);

export function useConversationalAIPageAgent() {
  const context = useContext(ConversationalAIPageAgentContext);
  if (!context) {
    throw new Error("useConversationalAIPageAgent must be used within a ConversationalAIPageAgentProvider");
  }
  return context;
}

interface ConversationalAIPageAgentProviderProps {
  children: ReactNode;
}

export function ConversationalAIPageAgentProvider({ children }: ConversationalAIPageAgentProviderProps) {
  const startCallActionsRef = useRef<(() => void)[]>([]);

  const registerStartCallAction = useCallback((action: () => void) => {
    startCallActionsRef.current.push(action);
  }, []);

  const startCall = useCallback(() => {
    // Execute all registered start call actions
    startCallActionsRef.current.forEach(action => action());
  }, []);

  return (
    <ConversationalAIPageAgentContext.Provider value={{ registerStartCallAction, startCall }}>
      {children}
    </ConversationalAIPageAgentContext.Provider>
  );
}
